import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LuChevronLeft,
  LuLayoutDashboard,
  LuLogOut,
  LuUserCog,
  LuSettings,
  LuShoppingCart,
  LuTag,
  LuUsers,
  LuUtensils,
  LuWallet,
} from "react-icons/lu";
import { userService } from "../services/userService";
import { translateText, translateLabel, useTranslationSnapshot } from "./../translations/runtime";


const getProfileDetails = () => {
  const payload = userService.getUserPayload() || {};
  const savedProfile = userService.getUserObject() || {};
  const payloadUser = payload.user || {};
  const fullName =
    payload.name ||
    payload.fullName ||
    payloadUser.name ||
    payloadUser.fullName ||
    savedProfile.name ||
    savedProfile.fullName ||
    [payload.firstName, payload.lastName].filter(Boolean).join(" ") ||
    payload.username ||
    payload.preferred_username ||
    savedProfile.userName ||
    "Vendor";
  const initials =
    fullName
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase() || "V";

  return { fullName, initials };
};

const VENDOR_NAV_SECTIONS = [
  {
    items: [
      {
        key: "dashboard",
        label: "Dashboard",
        icon: LuLayoutDashboard,
        path: "/vendor-dashboard",
        previewPath: "/vendor-dashboard-preview",
      },
      {
        key: "menu",
        label: "Menu Management",
        icon: LuUtensils,
        path: "/vendor-menu",
        previewPath: "/vendor-menu-preview",
      },
      // {
      //   key: "orders",
      //   label: "Orders",
      //   icon: LuShoppingCart,
      //   path: "/vendor-orders",
      //   previewPath: "/vendor-orders-preview",
      // },
    ],
  },
  {
    items: [
      // {
      //   key: "promotions",
      //   label: "Promotions",
      //   icon: LuTag,
      //   path: "/vendor-promotions",
      //   previewPath: "/vendor-promotions-preview",
      // },
    ],
  },
  {
    items: [
      {
        key: "storefront",
        label: "Storefront Settings",
        icon: LuSettings,
        path: "/vendor-storefront",
        previewPath: "/vendor-storefront-preview",
      },
    ],
  },
  {
    items: [
      // {
      //   key: "users",
      //   label: "User Management",
      //   icon: LuUsers,
      //   path: "/vendor-user-management",
      //   previewPath: "/vendor-user-management-preview",
      // },
      // {
      //   key: "wallet",
      //   label: "Digital Wallet",
      //   icon: LuWallet,
      //   path: "/vendor-digital-wallet",
      //   previewPath: "/vendor-digital-wallet-preview",
      // },
      {
        key: "account-settings",
        label: "Account Settings",
        icon: LuUserCog,
        path: "/vendor-account-settings",
        previewPath: "/vendor-account-settings-preview",
      },
      // {
      //   key: "more",
      //   label: "More",
      //   icon: LuEllipsis,
      // },
    ],
  },
];

const MOBILE_SIDEBAR_QUERY = "(max-width: 1024px)";

const isMobileSidebar = () =>
  typeof window !== "undefined" &&
  window.matchMedia(MOBILE_SIDEBAR_QUERY).matches;

function VendorSidebar({ activeKey, isPreview = false }) {
  useTranslationSnapshot();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => isMobileSidebar());
  const profile = getProfileDetails();

  const handleLogout = () => {
    userService.logout();
    navigate("/sign-in", { replace: true });
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia(MOBILE_SIDEBAR_QUERY);
    const syncSidebar = (matches) => setCollapsed(matches);
    const handleChange = (event) => syncSidebar(event.matches);

    syncSidebar(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return (
    <aside
      className={`vendor-flow-sidebar ${
        collapsed ? "vendor-flow-sidebar-collapsed" : ""
      }`}
    >
      <button
        className="vendor-flow-collapse-button"
        type="button"
        aria-label={translateText(collapsed ? "Expand vendor menu" : "Collapse vendor menu")}
        onClick={() => setCollapsed((current) => !current)}
      >
        <LuChevronLeft className="navigation-direction-icon" aria-hidden="true" />
      </button>

      <div className="vendor-flow-sidebar-profile">
        <span className="vendor-flow-avatar">{profile.initials}</span>
        {!collapsed && (
          <span className="vendor-flow-profile-name">{profile.fullName}</span>
        )}
      </div>

      <nav className="vendor-flow-navigation" aria-label={translateLabel("txt_web_vendor_vendor_navigation", "Vendor navigation")}>
        {VENDOR_NAV_SECTIONS.map((section, sectionIndex) => (
          <React.Fragment key={section.label || `section-${sectionIndex}`}>
            {section.label && !collapsed && (
              <div className="vendor-flow-section-label">{translateText(section.label)}</div>
            )}
            {section.items.map((item) => {
              const Icon = item.icon;
              const isActive = activeKey === item.key;
              const itemPath = isPreview ? item.previewPath : item.path;
              const isAvailable = Boolean(itemPath);
              const ItemElement = isAvailable ? Link : "span";

              return (
                <ItemElement
                  className={`vendor-flow-nav-item ${
                    isActive ? "vendor-flow-nav-item-active" : ""
                  } ${!isAvailable ? "vendor-flow-nav-item-upcoming" : ""}`}
                  {...(isAvailable ? { to: itemPath } : {})}
                  {...(isAvailable
                    ? {
                        onClick: () => {
                          if (isMobileSidebar()) setCollapsed(true);
                        },
                      }
                    : {})}
                  aria-current={isActive ? "page" : undefined}
                  aria-disabled={!isAvailable}
                  key={item.key}
                  title={translateText(collapsed ? item.label : undefined)}
                >
                  <Icon aria-hidden="true" />
                  {!collapsed && <span>{translateText(item.label)}</span>}
                </ItemElement>
              );
            })}
          </React.Fragment>
        ))}
      </nav>

      <button
        className="vendor-flow-nav-item vendor-flow-logout-button"
        type="button"
        onClick={handleLogout}
        title={translateText(collapsed ? "Logout" : undefined)}
      >
        <LuLogOut aria-hidden="true" />
        {!collapsed && <span>{translateLabel("txt_common_logout", "Logout")}</span>}
      </button>
    </aside>
  );
}

export default VendorSidebar;
